import React from 'react';
import { CircularProgress } from '@mui/material';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import useTimelineScale from '@src/hooks/useTimeline';
import { BLOCK_VARIANT } from '@src/constants/timeline';
import { COLOR } from '@src/styles/color';
import Waveform from '@src/new-components/Timeline/Waveform';
import { useTranslation } from 'react-i18next';
import {
  StyledClip,
  StyledClipText,
  StyledIconBadge,
  StyledTrimBar,
  StyledTrimHandle,
  StyledWaveformBaseline,
  StyledWaveformFill,
  StyledWaveformSlot,
} from './index.style';

/**
 * A block on a lane, positioned/sized by time. `variant` is the status look
 * (default | error | trimmed) and `selected` is the independent selection
 * border/handles — a trimmed clip can also be selected.
 *
 * The leading glyph is an eq icon for default/error and a scissors icon for a
 * trimmed clip; only the trimmed clip wraps it in a filled navy pill.
 * - error: red border + tint, only the frame/fill turns red.
 * - trimmed: charcoal fill + scissors pill (audio plays longer than its slot,
 *   so the tail is cut).
 *
 * The row below the text shows the block's audio waveform whenever it has
 * playable audio (`hasAudio`, trimmed or not); a block with no audio keeps the
 * "no audio yet" stripes, and an error clip shows neither. `waveform` is the
 * decoded audio ({ peaks, duration }); it draws only across the fraction of the
 * slot the audio spans, so the envelope renders at the timeline's px-per-second
 * (a trimmed clip's cut tail is dropped; a short audio doesn't stretch to fill a
 * longer slot). Audio but no peaks yet — the clip is too narrow for the decode
 * to be worth it, or the decode is still running — draws a flat baseline rather
 * than leaving the slot blank.
 *
 * Width is the block's true time-span in px (no artificial minimum) so adjacent
 * blocks touch instead of overlapping when zoomed out.
 */
const Clip = ({
  id,
  startSec,
  endSec,
  text,
  variant = BLOCK_VARIANT.DEFAULT,
  selected = false,
  hasAudio = false,
  waveform = null,
  draftRange = null,
  onHandlePointerDown,
  onBodyPointerDown,
  onContextMenu,
  loading = false,
  isLocked = false,
  isPlaying = false,
}) => {
  const { t } = useTranslation();
  const { secToPx } = useTimelineScale();
  // While dragging a handle, render from the live draft position instead of the
  // committed block timing, so the clip resizes smoothly before release.
  const drawStartSec = draftRange ? draftRange.startSec : startSec;
  const drawEndSec = draftRange ? draftRange.endSec : endSec;
  const left = secToPx(drawStartSec);
  const width = Math.max(secToPx(drawEndSec) - secToPx(drawStartSec), 0);
  const isTrimmed = variant === BLOCK_VARIANT.TRIMMED;
  // Playable audio → waveform; no audio → stripes; an error overrides both.
  const hasWaveform = hasAudio && variant !== BLOCK_VARIANT.ERROR;
  // While the block's audio is being generated the clip is locked: a spinner
  // replaces the status glyph, no trim handles, no stripes, and every pointer
  // interaction (select / trim / move) is suppressed.

  // Seconds of audio that actually play inside the slot (a trimmed clip cuts the
  // tail). Drives both the peaks shown and the drawn width, so the waveform
  // renders at the timeline's px-per-second. Memoised so the SVG path is rebuilt
  // only on a real resize/re-decode, not on the playback tick.
  // The waveform is pinned to the clip's left and sized in ABSOLUTE px from the
  // audio's true duration at the timeline's px-per-second (START_OFFSET cancels
  // in the difference). A trimmed clip (audio longer than its slot) overflows,
  // and the slot clips the tail (overflow:hidden). Absolute px — not a % of the
  // slot's margin-inset width — is what makes it pixel-stable: during a
  // right-edge trim only drawEndSec moves, so this width and the left origin
  // stay constant and the whole (WHOLE-peaks, stable-path) envelope is frozen,
  // with just the clip window sliding over it. No re-layout, no drift.
  const pxPerSec = secToPx(1) - secToPx(0);
  const waveformWidthPx = waveform ? waveform.duration * pxPerSec : 0;
  // Converting audio, a running export, or project playback each make the clip
  // read-only: no select, move, trim or menu. Only the spinner is tied to
  // `loading` alone.
  //
  // Note this does NOT drop pointer-events (only `loading`/`isLocked` do, in
  // the style): while playing, the clip must still swallow the press. Letting it
  // fall through would hand it to the timeline's seek surface, so clicking a
  // clip mid-playback would scrub — and "a click does nothing while playing" is
  // exactly what the focus rule established.
  const isInert = loading || isLocked || isPlaying;

  return (
    <StyledClip
      type="button"
      // Owns its own gesture (select / move / trim) — opts the clip out of the
      // timeline's click-anywhere-to-seek surface, see services/isSeekSurface.
      data-timeline-clip
      variant={variant}
      selected={selected}
      hasWaveform={hasWaveform}
      loading={loading}
      isLocked={isLocked}
      isPlaying={isPlaying}
      // A draft range only exists while this clip is being moved or trimmed —
      // the same condition the music track drives its cursor from.
      dragging={Boolean(draftRange)}
      aria-busy={loading}
      aria-disabled={isInert}
      style={{ left, width }}
      onPointerDown={
        isInert ? undefined : (event) => onBodyPointerDown?.(event, id)
      }
      onContextMenu={
        isInert ? undefined : (event) => onContextMenu?.(event, id)
      }
    >
      {selected && !isInert && (
        <StyledTrimHandle
          side="left"
          onPointerDown={(event) => onHandlePointerDown?.(event, id, 'start')}
        >
          <StyledTrimBar />
        </StyledTrimHandle>
      )}
      <StyledClipText variant={variant} noContent={!text}>
        <StyledIconBadge isTrimmed={isTrimmed}>
          {loading ? (
            <CircularProgress size={12} sx={{ color: COLOR.label[64] }} />
          ) : (
            <>
              {isTrimmed ? <ContentCutRoundedIcon /> : <GraphicEqRoundedIcon />}
            </>
          )}
        </StyledIconBadge>
        <span>{text || t('noContent')}</span>
      </StyledClipText>
      {hasWaveform && (
        <StyledWaveformSlot aria-hidden>
          {waveform ? (
            <StyledWaveformFill style={{ width: waveformWidthPx }}>
              <Waveform peaks={waveform.peaks} color={COLOR.label[64]} />
            </StyledWaveformFill>
          ) : (
            // No peaks (too narrow to decode, or still decoding) — a flat
            // baseline instead of an empty slot.
            <StyledWaveformBaseline />
          )}
        </StyledWaveformSlot>
      )}
      {selected && !isInert && (
        <StyledTrimHandle
          side="right"
          onPointerDown={(event) => onHandlePointerDown?.(event, id, 'end')}
        >
          <StyledTrimBar />
        </StyledTrimHandle>
      )}
    </StyledClip>
  );
};

// Memoised: with a windowed lane + stable props (id/handlers/waveform), clips
// don't re-render on unrelated parent renders (e.g. the 100ms playback tick) —
// only when their own position/variant/selection/waveform actually changes.
export default React.memo(Clip);
